import classNames from "classnames"
import { BackgroundDesignator, borderShapeClasses, BorderShape } from "components/DesignSystem"
import { determineBackgroundClass } from "components/DesignSystemUtils/determineBackgroundClass"
import { PropsWithChildren } from "react"

export interface PaperBoxProps extends PropsWithChildren {
  /**
   *  The background color of the box.
   */
  background?: BackgroundDesignator
  /**
   *  Additional classes applied to the wrapper, e.g. padding or flex utilities
   *  added by composing components such as `Paper`.
   */
  className?: string
  /**
   *  Forwarded to the wrapper `<div>` for anchor/scroll-to targets.
   */
  id?: string
  /**
   *  Elevates the box with a drop shadow while hovered (e.g. cards that
   *  reveal actions on hover).
   */
  elevateOnHover?: boolean
  /**
   *  Corner radius of the box.
   *  @default "border/shape/16" (rounded-2xl)
   */
  shape?: BorderShape
}

/**
 *  This is a component that presents a box with rounded corners and white
 *  background. We have another component, Paper, which does conceptually
 *  the same, but it adds additional flex, padding, and text alignment.
 *  In some cases we just need the box.
 *
 *  For layouts without the paper styling (no border), use SplitPanel directly.
 */
export function PaperBox({
  background = "background/default",
  className,
  id,
  elevateOnHover,
  shape = "border/shape/16",
  children,
}: PaperBoxProps) {
  const backgroundClass = determineBackgroundClass(background)
  return (
    <div
      id={id}
      className={classNames(
        borderShapeClasses[shape],
        "border border-gray-100 bg-clip-border",
        elevateOnHover && "transition-shadow hover:shadow",
        backgroundClass,
        className
      )}
    >
      {children}
    </div>
  )
}
